import {
  ArrowDownToLine,
  ArrowRight,
  ClipboardCheck,
  FileCheck2,
  MapPinned,
  Radar,
  ShieldAlert,
  ShieldCheck,
  TimerReset,
  Users,
} from "lucide-react";

import { Link } from "react-router-dom";

import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { SectionHeading } from "@/components/SectionHeading";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";

const councilPillars = [
  {
    title: "Contract readiness",
    icon: FileCheck2,
    body: "CareFleet presents a clear operating model with service visibility, governance support, and structured communication.",
  },
  {
    title: "Safeguarding compliance",
    icon: ShieldAlert,
    body: "Processes are designed to support secure handovers, documented incidents, and confident safeguarding assurance.",
  },
  {
    title: "Risk reduction",
    icon: TimerReset,
    body: "Real-time oversight and clearer escalation pathways help councils reduce uncertainty around passenger journeys.",
  },
  {
    title: "Operational visibility",
    icon: Radar,
    body: "Guardian provides a provider view that councils can understand through a more structured, monitorable service model.",
  },
];

export const CouncilsPage = () => {
  const downloadCapabilityStatement = () => {
    const content = [
      "CareFleet Transport Ltd — Capability Statement (Placeholder)",
      "",
      "Positioning:",
      "CareFleet has been designed from the ground up to deliver structured, safeguarding-focused transport with real-time visibility.",
      "",
      "Core areas:",
      "- Specialist SEND and vulnerable transport",
      "- Safeguarding-first operating design",
      "- Technology-enabled operations through CareFleet Guardian",
      "- Structured communication and incident reporting workflows",
      "",
      "This is a placeholder download for the live website build.",
    ].join("\n");

    const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "carefleet-capability-statement-placeholder.txt";
    anchor.click();
    URL.revokeObjectURL(url);
    toast.success("Capability statement placeholder downloaded");
  };

  return (
    <div>
      <PageHero
        eyebrow="For Councils / Procurement"
        title="A highly structured transport service built for safeguarding, visibility, and operational clarity."
        description="CareFleet has been designed from the ground up to deliver structured, safeguarding-focused transport with real-time visibility. The focus is on clear system design, risk-aware planning, and transparent operational checkpoints."
        highlights={["Safeguarding-first design", "Real-time tracking enabled", "Structured workflows", "Risk-aware operations"]}
        actions={[
          { label: "Request Demo", to: "/contact", testId: "procurement-request-demo-button" },
          { label: "Discuss Procurement Requirements", to: "/contact", testId: "procurement-contact-partnership-button", variant: "outline" },
        ]}
        baseTestId="councils-hero"
        visual={
          <div className="dashboard-surface p-8">
            <div className="grid gap-4 sm:grid-cols-2">
              {[
                "Live journey tracking",
                "Parent visibility",
                "School coordination",
                "Admin oversight",
              ].map((item) => (
                <div key={item} className="rounded-3xl border border-white/10 bg-white/5 p-5" data-testid={`procurement-platform-${item.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <p className="font-heading text-2xl font-semibold text-white">{item}</p>
                  <p className="mt-3 text-sm leading-6 text-slate-300">Illustrative operational view showing how visibility supports safeguarding and control.</p>
                </div>
              ))}
            </div>
          </div>
        }
      />

      <section className="px-4 py-24 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl space-y-12">
          <SectionHeading
            eyebrow="Capability statement"
            title="Specialist transport capability shaped around safeguarding-first design and technology-enabled operations."
            description="CareFleet is positioned for specialist SEND and vulnerable passenger transport, combining structured transport workflows with CareFleet Guardian for digital visibility and operational control."
            testId="councils-pillars-heading"
          />

          <div className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
            <Reveal>
              <div className="soft-card h-full p-8" data-testid="procurement-capability-card">
                <p className="eyebrow">Positioning</p>
                <h2 className="mt-4 font-heading text-3xl font-semibold text-slate-950">
                  Built with structure and clarity rather than vague transport promises.
                </h2>
                <p className="mt-4 text-base leading-8 text-slate-600">
                  CareFleet has been designed in alignment with modern safeguarding, visibility, and accountability expectations. The operating model emphasises structure, clear communication, and risk-aware journey management.
                </p>
                <div className="mt-8 grid gap-4 sm:grid-cols-2">
                  {[
                    "Specialist SEND and vulnerable transport",
                    "Safeguarding-first design",
                    "Technology-enabled operations",
                    "Structured transport workflows",
                  ].map((item) => (
                    <div key={item} className="rounded-3xl border border-slate-200 bg-slate-50 p-5" data-testid={`procurement-capability-${item.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                      <p className="font-medium text-slate-800">{item}</p>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="soft-card flex h-full flex-col justify-between p-8" data-testid="procurement-download-card">
                <div>
                  <p className="eyebrow">Download block</p>
                  <h3 className="mt-4 font-heading text-3xl font-semibold text-slate-950">Download Capability Statement</h3>
                  <p className="mt-4 text-base leading-8 text-slate-600">
                    Placeholder download for procurement review packs, internal circulation, or early-stage partnership discussions.
                  </p>
                </div>
                <Button
                  type="button"
                  onClick={downloadCapabilityStatement}
                  className="mt-8 rounded-full bg-brand-navy px-6 py-6 text-white hover:bg-brand-navy/90"
                  data-testid="procurement-download-capability-button"
                >
                  Download Capability Statement
                  <ArrowDownToLine className="h-4 w-4" />
                </Button>
              </div>
            </Reveal>
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            {councilPillars.map((pillar) => {
              const Icon = pillar.icon;
              return (
                <Reveal key={pillar.title}>
                  <div className="soft-card h-full p-8" data-testid={`councils-pillar-${pillar.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-mist text-brand-navy">
                      <Icon className="h-7 w-7" />
                    </div>
                    <h2 className="mt-6 font-heading text-3xl font-semibold text-slate-950">{pillar.title}</h2>
                    <p className="mt-4 text-base leading-8 text-slate-600">{pillar.body}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-brand-mist/60 px-4 py-24 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-6 lg:grid-cols-[0.95fr_1.05fr]">
          <Reveal>
            <div className="soft-card h-full p-8" data-testid="councils-outcomes-card">
              <p className="eyebrow">Safeguarding assurance</p>
              <h2 className="mt-4 font-heading text-3xl font-semibold text-slate-950">Designed to operate with safeguarding embedded into the workflow.</h2>
              <p className="mt-4 text-base leading-8 text-slate-600">
                DBS-checked staff requirements, driver training approach, secure handovers, and an incident reporting structure are treated as design standards for how the service operates — not as historical claims.
              </p>
              <p className="mt-4 text-base leading-8 text-slate-600">
                CareFleet has been designed in alignment with UK safeguarding expectations and duty of care principles.
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="dashboard-surface h-full p-8" data-testid="councils-monitoring-card">
              <p className="text-sm uppercase tracking-[0.2em] text-slate-300">Platform advantage</p>
              <div className="mt-6 grid gap-4 sm:grid-cols-2">
                {[
                  "Live journey awareness",
                  "Parent visibility",
                  "School coordination",
                  "Admin oversight",
                ].map((item) => (
                  <div key={item} className="rounded-3xl border border-white/10 bg-white/5 p-5" data-testid={`councils-monitoring-${item.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                    <p className="font-heading text-xl font-semibold text-white">{item}</p>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="px-4 py-24 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl space-y-12">
          <SectionHeading
            eyebrow="Operational approach"
            title="How CareFleet is designed to plan journeys, manage risk, and communicate clearly."
            description="This section replaces experience claims with a practical view of the operating model and the framework being built around it."
            testId="procurement-approach-heading"
          />

          <div className="grid gap-6 lg:grid-cols-2">
            {[
              {
                title: "How journeys are planned",
                icon: FileCheck2,
                body: "Journey planning is intended to prioritise passenger needs, route structure, timing clarity, and stakeholder visibility before travel begins.",
              },
              {
                title: "How risks are managed",
                icon: ShieldCheck,
                body: "Risk-aware design means safeguarding checkpoints, escalation logic, and clear accountability are built into the operating workflow.",
              },
              {
                title: "How communication is handled",
                icon: Users,
                body: "Parents, schools, and internal teams are supported by clearer status updates, incident pathways, and role-based visibility through Guardian.",
              },
              {
                title: "How safeguarding is embedded",
                icon: ClipboardCheck,
                body: "Safeguarding is treated as an operational design principle, influencing handovers, reporting structure, and journey controls through clear operational checkpoints.",
              },
            ].map((item) => {
              const Icon = item.icon;
              return (
                <Reveal key={item.title}>
                  <div className="soft-card h-full p-8" data-testid={`procurement-approach-${item.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-mist text-brand-navy">
                      <Icon className="h-7 w-7" />
                    </div>
                    <h3 className="mt-6 font-heading text-3xl font-semibold text-slate-950">{item.title}</h3>
                    <p className="mt-4 text-base leading-8 text-slate-600">{item.body}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>

          <div className="grid gap-6 lg:grid-cols-[1fr_1fr]">
            <Reveal>
              <div className="soft-card h-full p-8" data-testid="procurement-compliance-card">
                <p className="eyebrow">Compliance & readiness</p>
                <h3 className="mt-4 font-heading text-3xl font-semibold text-slate-950">Operational and compliance framework in development and aligned to sector standards</h3>
                <div className="mt-6 grid gap-4 sm:grid-cols-2">
                  {["Policies", "Insurance", "Risk assessments", "Health & safety"].map((item) => (
                    <div key={item} className="rounded-3xl border border-slate-200 bg-slate-50 p-5" data-testid={`procurement-compliance-${item.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                      <p className="font-medium text-slate-800">{item}</p>
                      <p className="mt-2 text-sm leading-6 text-slate-600">Placeholder for framework documentation and readiness materials.</p>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="dashboard-surface h-full p-8" data-testid="procurement-day-one-card">
                <p className="text-sm uppercase tracking-[0.2em] text-slate-300">Day-one operating principles</p>
                <div className="mt-6 space-y-4">
                  {[
                    "Safeguarding-first design",
                    "Real-time tracking enabled",
                    "Structured transport workflows",
                    "Full operational transparency through real-time oversight",
                  ].map((item) => (
                    <div key={item} className="flex items-center gap-3 rounded-3xl border border-white/10 bg-white/5 p-5" data-testid={`procurement-principle-${item.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                      <MapPinned className="h-5 w-5 text-brand-teal" />
                      <p className="font-medium text-white">{item}</p>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="px-4 pb-24 sm:px-6 lg:px-8">
        <Reveal>
          <div className="mx-auto flex max-w-7xl flex-col gap-6 rounded-[32px] bg-brand-navy px-8 py-10 text-white shadow-[0_35px_110px_-55px_rgba(30,58,138,0.85)] lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p className="eyebrow text-white/80" data-testid="procurement-cta-eyebrow">Next step</p>
              <h2 className="mt-3 font-heading text-3xl font-semibold" data-testid="procurement-cta-title">
                Explore CareFleet as a safeguarding-focused, technology-enabled procurement conversation.
              </h2>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row">
              <Button asChild className="rounded-full bg-white px-6 py-6 text-brand-navy hover:bg-slate-100">
                <Link to="/contact" data-testid="procurement-bottom-request-demo-button">
                  Request Demo
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" className="rounded-full border-white/30 bg-transparent px-6 py-6 text-white hover:bg-white/10">
                <Link to="/contact" data-testid="procurement-bottom-contact-partnership-button">
                  Speak to Our Team
                </Link>
              </Button>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
};